import { useState, ReactElement, useEffect, FC, useMemo } from 'react';
import { Field, Form, useFormState } from 'react-final-form';
import CreatableSelect from 'react-select/creatable';
import { Form as BootstrapForm } from 'react-bootstrap';
import { Editor } from 'react-draft-wysiwyg';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { Box, Button, StyledComponent, BoxProps, withDefaults, RFFErrorMessage, RFFTextInput, getReactSelectStyles } from '@qwealth/qcore';
import { QLog, selectUser } from '@qwealth/qdata';
import { commonSubscription, DocumentUploader, RFFMaskedDateField } from '@qwealth/kyc';
import { deleteFiles, uploadFiles, type ContactListItem } from '@qwealth/qvault';

import { useAppDispatch } from 'data/store';
import { getInitialCallLog, QLogForm, toQLog } from 'data/models/logForm';
import { saveActivityLog } from 'data/actions/activities';
import { saveHouseholdLog } from 'data/actions/householdActivities';
import { type QLogCreatePayload } from 'data/data-layer/callLogs';
import { validateLog } from 'data/validations/logValidations';
import { getLogAttachmentPath } from 'data/helpers/logHelper';
import { buildAllMembers, getCheckedMemberQids } from 'data/helpers/meaningfulInteraction';
import { useInitializedEditorState } from 'hooks/useInitializedEditorState';
import { useQvault2Flag } from 'hooks/useFeatureFlag';
import { encodeFilesAsBase64 } from 'data/helpers/attachmentHelper';
import HouseholdSearch from 'components/common/HouseholdSearch';
import KYCFlags from 'components/common/KYCFlags';
import { EditorWrapper } from 'components/common/EditorWrapper';
import AttachmentList from 'components/common/AttachmentList';
import { UploaderWithoutPreview } from 'components/common/UploaderWithoutPreview';
import { deleteEmbeddedAttachments } from 'data/data-layer/attachments';
import LinkedHousehold from './LinkedHousehold';
import TransitionBox from 'components/Households/Common/QNotes/TransitionBox';
import { LOG_TYPES } from 'data/models/Activity';
import { LogActions } from 'components/common/activityActions';
import { MeaningfulContent } from '../../MeaningfulContent';
import { loadHouseholdMetadata } from 'data/actions/householdMetadata';
import { selectClientMembers, selectHouseholdLegalEntities } from '../../../../data/selectors/householdSelectors';

const Label = withDefaults<BoxProps>({
  as: 'label',
  display: 'grid',
  gridGap: 'small',
})(Box) as StyledComponent<BoxProps, HTMLLabelElement>;

interface Props {
  log: Partial<QLog> | null;
  isQFeed?: boolean;
  close?: () => void;
  logActions?: LogActions;
}

const MeaningfulContentWrapper: React.FC<{ allMembers: ContactListItem[]; disabledQids: string[] }> = ({ allMembers, disabledQids }) => {
  const dispatch = useDispatch();
  const { values: { householdQID } } = useFormState<QLogForm>();
  const householdQIDString = (typeof householdQID === 'string' ? householdQID : householdQID?.value) || '';

  useEffect(() => {
    if (householdQIDString) {
      dispatch(loadHouseholdMetadata(householdQIDString));
    }
  }, [dispatch, householdQIDString]);

  return <MeaningfulContent allMembers={allMembers} disabledQids={disabledQids} />;
}

const AddLog: FC<Props> = ({ log, isQFeed, close, logActions }): ReactElement => {
  const clientMembers = useSelector(selectClientMembers);
  const legalEntities = useSelector(selectHouseholdLegalEntities);
  const allMembers = useMemo(() => buildAllMembers(clientMembers, legalEntities), [clientMembers, legalEntities]);

  const dispatch = useAppDispatch();
  const user = useSelector(selectUser);
  const email = user.email ?? '';
  const canChangeMeetingDate = log?.meetingDate === undefined;
  const canChangeSubject = log?.title === undefined;
  const [qvault2Enabled, qvault2Loaded] = useQvault2Flag();
  const existingAttachments = log?.attachments ?? [];

  const [editorState, setEditorState] = useInitializedEditorState(log);
  const [logAttachmentsPath, setLogAttachmentsPath] = useState<string>('');
  const [queuedDeletionIds, setQueuedDeletionIds] = useState<string[]>([]);
  const [initialCallLog, setInitialCallLog] = useState<QLog>(() => getInitialCallLog(email, log));
  const isHouseholdQIDValidationRequired = !initialCallLog.uuid && !!isQFeed;

  const TransitionBoxKey = useMemo(() => initialCallLog.uuid ?? uuidv4(), [initialCallLog.uuid]);

  const saveLog = async (rawValues: QLogForm) => {
    const { relatedMembersChecked, selectAllRelatedMembers, attachmentsToDelete, ...cleanValues } = rawValues;
    const relatedMembers = getCheckedMemberQids(allMembers, relatedMembersChecked);

    const basePayload = { ...toQLog(cleanValues, editorState), relatedMembers };
    const payload: QLog | QLogCreatePayload = qvault2Enabled && rawValues.pending?.length
      ? { ...basePayload, attachments: await encodeFilesAsBase64(rawValues.pending) }
      : basePayload;

    const saved = await (isQFeed ? dispatch(saveActivityLog(payload)) : dispatch(saveHouseholdLog(payload)));
    if (!saved) {
      return;
    }

    if (!qvault2Enabled && rawValues.pending?.length && saved.uuid && saved.owners?.[0]) {
      await uploadFiles(rawValues.pending, getLogAttachmentPath(saved.owners[0], saved.uuid));
    }

    if (attachmentsToDelete?.length) {
      await deleteFiles(attachmentsToDelete);
    }
    if (queuedDeletionIds.length && log?.owners?.[0]) {
      await deleteEmbeddedAttachments(queuedDeletionIds, log.owners[0]);
      setQueuedDeletionIds([]);
    }

    logActions?.cancelLog?.();
    close?.();
  };

  const disabled = !!log && !!log.uuid;

  useEffect(() => {
    if (qvault2Loaded && !qvault2Enabled && log?.uuid && log.owners && log.owners[0]) {
      const path = getLogAttachmentPath(log.owners[0], log.uuid);
      setLogAttachmentsPath(path);
    } else {
      setLogAttachmentsPath('');
    }
  }, [log, qvault2Enabled, qvault2Loaded]);

  useEffect(() => {
    setInitialCallLog(getInitialCallLog(email, log));
  }, [email, log]);

  const handleOnCancel = () => {
    if (close) {
      close();
    }
    if (logActions && logActions.cancelLog) {
      logActions.cancelLog();
    }
  };

  return (
    <Form<QLogForm>
      key={TransitionBoxKey}
      initialValues={initialCallLog}
      onSubmit={saveLog}
      validate={values => validateLog(values, editorState, isHouseholdQIDValidationRequired)}
    >
      {({ handleSubmit, submitting, values }) => {
        return (
          <form onSubmit={handleSubmit}>
            <TransitionBox index={1} isVisible={true} key={TransitionBoxKey}>
              <Box>
                {isQFeed && (
                  <Box display="flex" flexDirection="column" mb="default">
                    <Label textStyle="formLabel">Household*</Label>
                    {disabled ? (
                      <LinkedHousehold resource={log} />
                    ) : (
                      <Field name="householdQID" subscription={commonSubscription}>
                        {({ input, meta }) => (
                          <Box style={{ zIndex: 100 }}>
                            <HouseholdSearch
                              {...input}
                              onChange={opt => input.onChange({ value: opt.value, label: opt.label })}
                              disabled={disabled}
                              customStyles={getReactSelectStyles(!!meta.touched && !!meta.error)}
                            />
                            {meta.touched && meta.error ? <RFFErrorMessage errorNotificationUUID={input.name}>{meta.error}</RFFErrorMessage> : null}
                          </Box>
                        )}
                      </Field>
                    )}
                  </Box>
                )}

                <Box display="grid" gridTemplateColumns="1fr 1fr" gap="default" mb="default">
                  <Box display="flex" flexDirection="column" flex={1} mb="default">
                    <Label textStyle="formLabel">Subject*</Label>
                    <Field name="title" subscription={commonSubscription}>
                      {({ input, meta }) => (
                        <>
                          <CreatableSelect
                            {...input}
                            onChange={opt => {
                              input.onChange(opt?.value);
                            }}
                            value={{ label: input.value, value: input.value }}
                            options={LOG_TYPES.map(t => ({ label: t, value: t }))}
                            isDisabled={!canChangeSubject}
                          />
                          {meta.touched && meta.error && <Box color="error">{meta.error}</Box>}
                        </>
                      )}
                    </Field>
                  </Box>

                  <Box display="flex" flexDirection="column" flex={1} mb="default" style={{ zIndex: 99 }}>
                    <Label textStyle="formLabel">Call Date*</Label>
                    <RFFMaskedDateField
                      name="meetingDate"
                      fieldSubscription={commonSubscription}
                      inputFieldProps={{
                        maxDate: new Date(),
                        disabled: !canChangeMeetingDate,
                      }}
                    />
                  </Box>

                  <Box display="flex" flexDirection="column" flex={1} mb="default">
                    <Label textStyle="formLabel">Share with Client</Label>
                    <Field name="isInternal">
                      {({ input }) => (
                        <BootstrapForm.Check
                          {...input}
                          type="switch"
                          style={{ zIndex: 0 }}
                          id="callLog.isInternal"
                          label=""
                          checked={input.value !== 1}
                          onChange={e => {
                            input.onChange(e.target.value === '1' ? 0 : 1);
                          }}
                        />
                      )}
                    </Field>
                  </Box>

                  <Box display="flex" flexDirection="column" flex={1} mb="default">
                    <RFFTextInput
                      name="author"
                      label="Created By"
                      subscription={commonSubscription}
                      inputFieldProps={{
                        disabled: true,
                      }}
                    />
                  </Box>
                </Box>

                <Box display="flex" flexDirection="column" mb="default">
                  <Label textStyle="formLabel">content*</Label>
                  <Field name="content" subscription={commonSubscription}>
                    {({ meta, input }) => (
                      <EditorWrapper minHeight={150} hasError={!!meta.error && meta.touched}>
                        <Editor
                          editorState={editorState}
                          handlePastedText={() => false}
                          onEditorStateChange={editorState => {
                            setEditorState(editorState);
                          }}
                          onBlur={() => {
                            input.onBlur();
                          }}
                          editorClassName="wysiwygEditor"
                          spellCheck
                        />
                        {meta.touched && meta.error && <RFFErrorMessage errorNotificationUUID={input.name}>{meta.error}</RFFErrorMessage>}
                      </EditorWrapper>
                    )}
                  </Field>
                </Box>

                {qvault2Loaded && (qvault2Enabled ? (
                  <>
                    <UploaderWithoutPreview>
                      <DocumentUploader
                        editing
                        label="Call Log Attachments"
                        title="Call Log Attachments"
                        directPath=""
                        isMulti
                      />
                    </UploaderWithoutPreview>
                    {existingAttachments.length > 0 && (
                      <Box mt="default">
                        <AttachmentList
                          attachments={existingAttachments}
                          householdQID={log?.owners?.[0]}
                          isFileDeletable={!values.isMeaningfulInteraction && !values.isSignificantChange && !values.isPortfolioReview}
                          label="Call Log Attachments"
                          onQueueDelete={id => setQueuedDeletionIds(prev => [...prev, id])}
                        />
                      </Box>
                    )}
                  </>
                ) : (
                  <DocumentUploader
                    editing
                    label="Call Log Attachments"
                    title="Call Log Attachments"
                    directPath={logAttachmentsPath}
                    isMulti
                    documentPreviewPosition="side"
                    isFileDeletable={!values.isMeaningfulInteraction && !values.isSignificantChange && !values.isPortfolioReview}
                  />
                ))}

                <KYCFlags kycFlags={initialCallLog} mt="large" mb="large" />

                {/* @ts-ignore TODO: remove after version bump */}
                <MeaningfulContentWrapper allMembers={allMembers} disabledQids={log?.relatedMembers || []} />

                <Box display="flex" gap="default" marginTop="large">
                  <Button type="submit" disabled={submitting}>
                    Save
                  </Button>
                  <Button variant="outline" onClick={handleOnCancel}>
                    Cancel
                  </Button>
                </Box>
              </Box>
            </TransitionBox>
          </form>
        );
      }}
    </Form>
  );
};

export default AddLog;
